// @systeme: interface
// Le lien d'invitation : « Juliette t'invite dans « Maison » ». Sans compte, on en crée
// un ; avec, on rejoint en un geste. Aucune autre information sur le foyer avant.
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as LienRouteur, useNavigate, useParams } from 'react-router';
import type { InfoInvitation } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { messageDe } from '../composants/Messages';
import { api } from '../lib/api';
import { useSession } from '../session/SessionContext';
import { tailles } from '../theme/theme';

export function Rejoindre() {
  const { jeton = '' } = useParams();
  const navigate = useNavigate();
  const { etat, mettreAJour } = useSession();
  const [info, setInfo] = useState<InfoInvitation | undefined>();
  const [erreur, setErreur] = useState<string | undefined>();
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.invitations
      .info(jeton)
      .then(setInfo)
      .catch((e: unknown) => setErreur(messageDe(e)));
  }, [jeton]);

  async function rejoindre() {
    setEnCours(true);
    try {
      mettreAJour(await api.invitations.accepter(jeton));
      void navigate('/');
    } catch (e) {
      setErreur(messageDe(e));
    } finally {
      setEnCours(false);
    }
  }

  if (erreur)
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', px: `${tailles.marge}px`, pt: 6 }}>
        <Typography variant="h1" component="h1" gutterBottom>
          Ce lien ne mène nulle part
        </Typography>
        <Typography color="text.secondary">{erreur}</Typography>
      </Box>
    );
  if (!info || etat.etat === 'chargement') return <Chargement />;

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: `${tailles.marge}px`, pt: 6 }}>
      <Typography variant="h1" component="h1" gutterBottom>
        {info.prenomInvitante} t’invite dans « {info.nomEspace} »
      </Typography>
      {!info.valide ? (
        <Typography color="text.secondary">
          Ce lien n’est plus valable. Demande une nouvelle invitation : ça prend dix secondes.
        </Typography>
      ) : etat.etat === 'connectee' ? (
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Tu es connectée en tant que {etat.session.utilisateur.prenom}. Rejoindre ce foyer
            partage ses tâches, ses rendez-vous et ses listes avec toi.
          </Typography>
          <Button
            variant="contained"
            size="large"
            disabled={enCours}
            onClick={() => void rejoindre()}
          >
            Rejoindre ce foyer
          </Button>
          <Button component={LienRouteur} to="/" color="inherit">
            Pas maintenant
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Les tâches, les rendez-vous, les listes : tout au même endroit, pour tout le foyer.
          </Typography>
          <Button
            component={LienRouteur}
            to={`/inscription?invitation=${encodeURIComponent(jeton)}`}
            variant="contained"
            size="large"
          >
            Créer mon compte
          </Button>
          <Button
            component={LienRouteur}
            to={`/connexion?invitation=${encodeURIComponent(jeton)}`}
            color="inherit"
          >
            J’ai déjà un compte
          </Button>
        </Stack>
      )}
    </Box>
  );
}
